import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface PlayerOption {
  id: string;
  playerIdCode?: string;
  firstName?: string;
  lastName?: string;
  primarySport?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  };
}

interface ResolvedBenchmark {
  playerId: string | null;
  playerName: string;
  photoUrl?: string;
  shortProfile?: string;
  totalScore: number;
  maxScore: number;
  scorePct: number;
  source: 'manual' | 'auto' | 'static';
}

interface CuratedBenchmark {
  id: string;
  playerId: string;
  playerName: string;
  photoUrl?: string;
  displayOrder: number;
  isActive: boolean;
}

interface StaticBenchmarkProfile {
  id?: string;
  displayName: string;
  shortProfile?: string;
  photoUrl?: string;
  basicScore: string;
  advancedScore: string;
  basicMaxScore: string;
  advancedMaxScore: string;
  displayOrder?: number;
  isActive?: boolean;
}

const SPORTS = ['Badminton', 'Cricket', 'Football', 'Tennis', 'Athletics'];
const TEST_TYPES = [
  { value: 'high-level', label: 'Basic Test' },
  { value: 'low-level', label: 'Advanced Test' },
];

const AdminLandingEliteBenchmarks: React.FC = () => {
  const [sport, setSport] = useState('Badminton');
  const [assessmentType, setAssessmentType] = useState('high-level');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [players, setPlayers] = useState<PlayerOption[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [resolved, setResolved] = useState<ResolvedBenchmark[]>([]);
  const [curated, setCurated] = useState<CuratedBenchmark[]>([]);
  const [staticProfiles, setStaticProfiles] = useState<StaticBenchmarkProfile[]>([
    { displayName: '', shortProfile: '', photoUrl: '', basicScore: '', advancedScore: '', basicMaxScore: '1000', advancedMaxScore: '1000' },
    { displayName: '', shortProfile: '', photoUrl: '', basicScore: '', advancedScore: '', basicMaxScore: '1000', advancedMaxScore: '1000' },
  ]);

  const authHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const playerName = (p: PlayerOption) =>
    [p.firstName || p.user?.firstName, p.lastName || p.user?.lastName].filter(Boolean).join(' ').trim() ||
    p.playerIdCode ||
    'Player';

  const filteredPlayers = useMemo(() => {
    return players.filter((p) => !p.primarySport || p.primarySport === sport);
  }, [players, sport]);

  const loadPlayers = async () => {
    const response = await fetch('/api/admin/players?limit=500');
    const data = await response.json();
    if (!data?.success) throw new Error(data?.message || 'Failed to load players');
    setPlayers(Array.isArray(data.data) ? data.data : []);
  };

  const loadBenchmarks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/admin/landing-elite-benchmarks?sport=${encodeURIComponent(sport)}&assessmentType=${encodeURIComponent(
          assessmentType
        )}&limit=2`,
        { headers: authHeaders() }
      );
      const data = await response.json();
      if (!data?.success) throw new Error(data?.message || 'Failed to load benchmark config');

      const otherAssessmentType = assessmentType === 'high-level' ? 'low-level' : 'high-level';
      const responseOther = await fetch(
        `/api/admin/landing-elite-benchmarks?sport=${encodeURIComponent(sport)}&assessmentType=${encodeURIComponent(
          otherAssessmentType
        )}&limit=2`,
        { headers: authHeaders() }
      );
      const dataOther = await responseOther.json();

      const curatedRows: CuratedBenchmark[] = Array.isArray(data?.data?.curated) ? data.data.curated : [];
      const resolvedRows: ResolvedBenchmark[] = Array.isArray(data?.data?.resolved) ? data.data.resolved : [];
      const staticRowsCurrent: any[] = Array.isArray(data?.data?.staticProfiles) ? data.data.staticProfiles : [];
      const staticRowsOther: any[] = Array.isArray(dataOther?.data?.staticProfiles) ? dataOther.data.staticProfiles : [];

      const highRows = assessmentType === 'high-level' ? staticRowsCurrent : staticRowsOther;
      const lowRows = assessmentType === 'low-level' ? staticRowsCurrent : staticRowsOther;
      const staticRows: StaticBenchmarkProfile[] = [0, 1].map((idx) => {
        const hi = highRows[idx];
        const lo = lowRows[idx];
        const base = hi || lo || {};
        return {
          id: base.id,
          displayName: base.displayName || '',
          shortProfile: base.shortProfile || '',
          photoUrl: base.photoUrl || '',
          basicScore: String(hi?.score ?? ''),
          advancedScore: String(lo?.score ?? ''),
          basicMaxScore: String(hi?.maxScore ?? base.maxScore ?? 1000),
          advancedMaxScore: String(lo?.maxScore ?? base.maxScore ?? 1000),
          displayOrder: base.displayOrder,
          isActive: base.isActive,
        };
      });

      setCurated(curatedRows);
      setResolved(resolvedRows);
      setStaticProfiles(staticRows);
      setSelectedPlayerIds(curatedRows.filter((c) => c.isActive).map((c) => c.playerId).slice(0, 2));
    } catch (err: any) {
      setError(err?.message || 'Failed to load benchmark config');
      setCurated([]);
      setResolved([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlayers().catch((err) => setError(err?.message || 'Failed to load players'));
  }, []);

  useEffect(() => {
    loadBenchmarks();
  }, [sport, assessmentType]);

  const togglePlayer = (id: string) => {
    setSelectedPlayerIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return prev;
      return [...prev, id];
    });
  };

  const updateStaticProfile = (index: number, patch: Partial<StaticBenchmarkProfile>) => {
    setStaticProfiles((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...patch } : p))
    );
  };

  const saveCurated = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch('/api/admin/landing-elite-benchmarks', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          sport,
          assessmentType,
          playerIds: selectedPlayerIds,
          staticProfiles: staticProfiles
            .filter((p) => p.displayName?.trim())
            .slice(0, 2),
        }),
      });
      const data = await response.json();
      if (!data?.success) throw new Error(data?.message || 'Failed to save benchmark config');
      setSuccess('Benchmark configuration saved');
      await loadBenchmarks();
    } catch (err: any) {
      setError(err?.message || 'Failed to save benchmark config');
    } finally {
      setSaving(false);
    }
  };

  const useAutoTopScorers = async () => {
    setSelectedPlayerIds([]);
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch('/api/admin/landing-elite-benchmarks', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ sport, assessmentType, playerIds: [] }),
      });
      const data = await response.json();
      if (!data?.success) throw new Error(data?.message || 'Failed to switch to auto mode');
      setSuccess('Switched to auto top scorers');
      await loadBenchmarks();
    } catch (err: any) {
      setError(err?.message || 'Failed to switch to auto mode');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Landing Elite Benchmarks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose up to 2 curated platform players, configure static benchmark profiles, or use automatic top scorers.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Sport</InputLabel>
            <Select value={sport} label="Sport" onChange={(e) => setSport(e.target.value)}>
              {SPORTS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Test Type</InputLabel>
            <Select value={assessmentType} label="Test Type" onChange={(e) => setAssessmentType(e.target.value)}>
              {TEST_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={saveCurated} disabled={saving}>
              Save Curated
            </Button>
            <Button variant="outlined" onClick={useAutoTopScorers} disabled={saving}>
              Use Auto
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Select Curated Players (max 2)
              </Typography>
              {loading ? (
                <CircularProgress size={22} />
              ) : (
                <Stack spacing={1}>
                  {filteredPlayers.slice(0, 100).map((p) => {
                    const id = p.id;
                    const selected = selectedPlayerIds.includes(id);
                    return (
                      <Box
                        key={id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          border: '1px solid #e5e7eb',
                          borderRadius: 1,
                          bgcolor: selected ? 'rgba(25,118,210,0.08)' : 'transparent',
                          cursor: 'pointer',
                        }}
                        onClick={() => togglePlayer(id)}
                      >
                        <Typography variant="body2">{playerName(p)}</Typography>
                        {selected && <Chip size="small" label="Selected" color="primary" />}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Static Benchmark Profiles (optional, max 2)
              </Typography>
              <Stack spacing={1.5}>
                {staticProfiles.map((p, idx) => (
                  <Box key={idx} sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Display Name"
                          value={p.displayName}
                          onChange={(e) => updateStaticProfile(idx, { displayName: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Photo URL"
                          value={p.photoUrl || ''}
                          onChange={(e) => updateStaticProfile(idx, { photoUrl: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Short Profile"
                          value={p.shortProfile || ''}
                          onChange={(e) => updateStaticProfile(idx, { shortProfile: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Basic Score"
                          value={p.basicScore}
                          onChange={(e) => updateStaticProfile(idx, { basicScore: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Advanced Score"
                          value={p.advancedScore}
                          onChange={(e) => updateStaticProfile(idx, { advancedScore: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Basic Max Score"
                          value={p.basicMaxScore}
                          onChange={(e) => updateStaticProfile(idx, { basicMaxScore: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Advanced Max Score"
                          value={p.advancedMaxScore}
                          onChange={(e) => updateStaticProfile(idx, { advancedMaxScore: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Current Landing Output
              </Typography>
              <Stack spacing={1.5}>
                {resolved.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No benchmark players resolved yet.
                  </Typography>
                ) : (
                  resolved.map((row, idx) => (
                    <Box
                      key={row.playerId || `static-${idx}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e5e7eb', borderRadius: 1 }}
                    >
                      <Avatar src={row.photoUrl || ''}>{row.playerName?.slice(0, 1)}</Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.playerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.totalScore} / {row.maxScore} ({row.scorePct}%)
                        </Typography>
                        {row.shortProfile && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {row.shortProfile}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={row.source === 'manual' ? 'Curated' : row.source === 'static' ? 'Static' : 'Auto'}
                        color={row.source === 'manual' ? 'primary' : row.source === 'static' ? 'secondary' : 'default'}
                      />
                    </Box>
                  ))
                )}
              </Stack>
              {curated.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Curated configured: {curated.length}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminLandingEliteBenchmarks;

